// Buyer "What you're looking for" preferences form. Captures budget, timeline,
// pre-approval status, location, home type, size, and their must-haves /
// dealbreakers list. Mirrors the BuyerHub reference schema.
import React, { useEffect, useRef, useState } from "react";
import { Field, useFormSubmit, FormFooter } from "./_shared";
import { api } from "../../lib/api";
import { useContent, DEFAULT_CONTENT } from "../../lib/content";
import { useBuyerContent } from "../../lib/buyerContent";
import { Save } from "lucide-react";

const DRAFT_ENDPOINT = "/forms/draft/buyer_preferences";

export default function BuyerPreferencesForm({ onSubmitted }) {
  const { buyerPreferencesSections: SECTIONS } = useBuyerContent();
  const [values, setValues] = useState({});
  const [savedAt, setSavedAt] = useState(null);
  const saveTimer = useRef(null);
  const loaded = useRef(false);
  const { submit, busy, err } = useFormSubmit("buyer_preferences");

  useEffect(() => {
    let alive = true;
    (async () => {
      try { const { data } = await api.get(DRAFT_ENDPOINT); if (alive) setValues(data.data || {}); }
      catch { /* no draft */ }
      finally { loaded.current = true; }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!loaded.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try { await api.put(DRAFT_ENDPOINT, { data: values }); setSavedAt(new Date()); }
      catch { /* silent draft save */ }
    }, 1000);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [values]);

  const upd = (k) => (e) => setValues((prev) => ({ ...prev, [k]: e.target.value }));

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); submit(values, onSubmitted); }}
      className="card"
      style={{ padding: 32, display: "flex", flexDirection: "column", gap: 28 }}
      data-testid="buyer-preferences-form"
    >
      <div style={{ color: "var(--text-muted)", fontSize: 14, lineHeight: 1.55, padding: "12px 16px", background: "#F0EDE3", borderRadius: 12, border: "1px solid var(--border)" }}>
        <b style={{ display: "block", marginBottom: 4, color: "var(--text)" }}>The more I know, the more your time gets respected.</b>
        Only homes that fit make the shortlist. Be as specific as you&rsquo;d like.
      </div>

      {SECTIONS.map((sec, i) => (
        <section key={i} data-testid={`buyer-preferences-section-${i}`}>
          <div className="overline" style={{ marginBottom: 6 }}>{String(i + 1).padStart(2, "0")} · {sec.title}</div>
          <div style={{ height: 1, width: 40, background: "var(--primary)", marginBottom: 20 }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {sec.fields.map((f, fi) => (
              <Field key={`${i}-${fi}-${f.key || ""}`} label={f.label}>
                {f.type === "textarea" ? (
                  <textarea data-testid={`buyer-preferences-${f.key}`} className="input-field" rows={4} value={values[f.key] || ""} onChange={upd(f.key)} placeholder={f.placeholder || ""} />
                ) : f.type === "select" ? (
                  <select data-testid={`buyer-preferences-${f.key}`} className="input-field" value={values[f.key] || ""} onChange={upd(f.key)}>
                    <option value="">Select…</option>
                    {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input data-testid={`buyer-preferences-${f.key}`} className="input-field" type={f.type} value={values[f.key] || ""} onChange={upd(f.key)} placeholder={f.placeholder || ""} />
                )}
              </Field>
            ))}
          </div>
        </section>
      ))}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <span style={{ fontSize: 12, color: "var(--text-muted)", display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Save size={12} /> {savedAt ? `Draft saved ${savedAt.toLocaleTimeString()}` : "Draft saves automatically as you type"}
        </span>
      </div>

      <FormFooter busy={busy} err={err} testid="buyer-preferences" />
    </form>
  );
}
